/// Workspace: Multi-tab workspace with TabBar and active EditorPanel or WelcomeTab.
/// Supports single, split-horizontal, and split-vertical layout modes.
///
/// IMPORTANT: All EditorPanels are rendered simultaneously to preserve WASM
/// worker state across tab switches. Inactive panels are hidden with
/// display:none so their workers, WebGL contexts, and OffscreenCanvas
/// instances stay alive. ResizeObserver in EditorCanvas already guards
/// against zero-dimension resize events from hidden panels.
///
/// PANEL ISOLATION: In split mode each panel pane maintains its own tab list
/// and active-tab selection via the editorStore panel state (panelTabs,
/// panelActiveTabId, focusedPanel). Click into a pane to focus it; new tabs,
/// toolbar actions, and keyboard shortcuts target the focused panel only.

import { useEffect, useRef, useCallback } from "react";
import { useEditorStore, type PanelId } from "@/stores/editorStore";
import { useUIStore } from "@/stores/uiStore";
import { TabBar } from "./TabBar";
import { EditorPanel } from "./EditorPanel";
import { WelcomeTab } from "./WelcomeTab";
import { SplitPane } from "./SplitPane";

// ---------------------------------------------------------------------------
// Single pane: TabBar + ALL EditorPanels (inactive ones hidden)
// ---------------------------------------------------------------------------

interface SinglePaneProps {
  /** Panel identity for per-panel state isolation. */
  panelId: PanelId;
}

function SinglePane({ panelId }: SinglePaneProps) {
  const tabs = useEditorStore((s) => s.tabs);
  const panelTabIds = useEditorStore((s) => s.panelTabs[panelId]);
  const panelActiveId = useEditorStore((s) => s.panelActiveTabId[panelId]);
  const setFocusedPanel = useEditorStore((s) => s.setFocusedPanel);

  // Filter tabs to those assigned to this panel
  const visibleTabs = tabs.filter((t) => panelTabIds.includes(t.id));

  // Active tab for this panel (must be in visible set)
  const effectiveActiveId =
    panelActiveId && visibleTabs.some((t) => t.id === panelActiveId)
      ? panelActiveId
      : (visibleTabs[0]?.id ?? null);

  const hasTabs = visibleTabs.length > 0;

  // Focus this panel on click
  const handlePanelFocus = useCallback(() => {
    setFocusedPanel(panelId);
  }, [panelId, setFocusedPanel]);

  return (
    <div data-testid="workspace" className="flex flex-1 flex-col min-h-0 overflow-hidden" onPointerDown={handlePanelFocus}>
      <TabBar panelId={panelId} />

      {/* Render ALL tabs to keep their WASM workers alive.
          Only the active tab is visible; inactive tabs are hidden via
          display:none which removes them from layout but keeps the
          React tree (and thus the workers) mounted. */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        {visibleTabs.map((tab) => {
          const isActive = tab.id === effectiveActiveId;
          return (
            <div
              key={tab.id}
              className="absolute inset-0"
              style={{ display: isActive ? "flex" : "none" }}
            >
              <EditorPanel tab={tab} panelId={panelId} />
            </div>
          );
        })}

        {!hasTabs && (
          <div className="flex-1 flex items-center justify-center bg-zinc-900 text-zinc-500 text-sm">
            No editor open in this pane
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main workspace
// ---------------------------------------------------------------------------

/**
 * Main workspace area.
 * Shows TabBar + active EditorPanel when tabs are open,
 * or WelcomeTab when no tabs exist.
 * Supports split view modes via SplitPane with isolated panel state.
 */
export function Workspace() {
  const tabs = useEditorStore((s) => s.tabs);
  const layoutMode = useUIStore((s) => s.layoutMode);
  const distributeTabs = useEditorStore((s) => s.distributeTabs);
  const prevLayoutRef = useRef(layoutMode);

  // Distribute tabs when layout mode changes
  useEffect(() => {
    if (prevLayoutRef.current !== layoutMode) {
      prevLayoutRef.current = layoutMode;
      distributeTabs(layoutMode === "single" ? "single" : "split");
    }
  }, [layoutMode, distributeTabs]);

  // Initial distribution on mount (ensure panel state is set up)
  useEffect(() => {
    distributeTabs(layoutMode === "single" ? "single" : "split");
  }, []); // Initial distribution on mount only

  // No tabs: show welcome regardless of layout mode
  if (tabs.length === 0) {
    return <WelcomeTab />;
  }

  // Single mode: one TabBar + one editor
  if (layoutMode === "single") {
    return <SinglePane panelId="single" />;
  }

  // Split modes: each pane has its own tab set and active tab
  const splitDirection = layoutMode === "split-horizontal" ? "horizontal" : "vertical";

  return (
    <SplitPane
      direction={splitDirection}
      first={<SinglePane panelId="first" />}
      second={<SinglePane panelId="second" />}
    />
  );
}
